import { useState } from "react"
import { DateRangePicker } from "../../shared/dateRangePicker"
import { SearchSelect } from "../../shared/searchSelect"
import { SearchFiltersModal } from "../../shared/searchFiltersModal"
import { DateGroupingTypeEnum, reportDatePresetByLabel } from "../reportsEnums"
import { dateRange, toDateString } from "../../../utilities/dateUtilities"
import stringHelpers from "../../../utilities/stringHelpers"
import arrayUtilities from "../../../utilities/arrayUtilities"
import Enums from "../../../utilities/enum"

export const BreachesReportFiltersModal = ({
    showModal,
    handleClose,
    handleConfirm,
    initialFromDate,
    initialToDate,
    initialDatePreset,
    initialSelectedSite,
    initialSelectedDateGrouping,
    initialSelectedStatus,
    initialAppliedFilters,
    sites = []
}) => {
    const [fromDate, setFromDate] = useState(initialFromDate)
    const [toDate, setToDate] = useState(initialToDate)
    const [datePreset, setDatePreset] = useState(initialDatePreset)
    const [selectedSite, setSelectedSite] = useState(initialSelectedSite)
    const [selectedDateGrouping, setSelectedDateGrouping] = useState(initialSelectedDateGrouping)
    const [selectedStatus, setSelectedStatus] = useState(initialSelectedStatus)
    const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters)

    const isDateRangeInvalid = fromDate === null || fromDate === "" || toDate === null || toDate === ""

    const defaultSiteId = !arrayUtilities.isNullOrEmpty(sites) ? sites[0].id : null
    const defaultSiteName = !arrayUtilities.isNullOrEmpty(sites) ? sites[0].name : null

    const clearLocalFilters = () => {
        const defaultRange = dateRange("lastMonth")
        setFromDate(defaultRange.firstDate)
        setToDate(defaultRange.lastDate)
        setDatePreset(reportDatePresetByLabel["Last month"])
        setSelectedSite(defaultSiteId)
        setSelectedDateGrouping(0)
        setSelectedStatus(0)
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`,
            "Site": defaultSiteName,
            "Date Grouping": DateGroupingTypeEnum[0],
            "Status": Enums.BreachStatus[0]
        })
    }

    return (
        <SearchFiltersModal
            title="Filter Results"
            handleClose={ handleClose }
            handleConfirm={ () => handleConfirm({ fromDate, toDate, datePreset, selectedSite, selectedDateGrouping, selectedStatus, appliedFilters }) }
            showModal={ showModal }
            key="breaches-report-filters-modal"
            handleReset={ clearLocalFilters }
            disableConfirm={ isDateRangeInvalid }
        >
            <div className="w-100">
                <div className="mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Date Range</label>
                    <DateRangePicker
                        fromDate={ fromDate }
                        toDate={ toDate }
                        setDateRange={ (firstDate, lastDate, presetLabel) => {
                            const isFirstDateEmpty = firstDate === null || firstDate === ""
                            const isLastDateEmpty = lastDate === null || lastDate === ""
                            setFromDate(isFirstDateEmpty ? null : firstDate);
                            setToDate(isLastDateEmpty ? null : lastDate);
                            setDatePreset(stringHelpers.isNullOrEmpty(presetLabel) ? null : (reportDatePresetByLabel[presetLabel] ?? null))
                            if (!stringHelpers.isNullOrEmpty(presetLabel)) {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    updatedFilters["Date Range"] = presetLabel
                                    return updatedFilters
                                })
                            }
                            else if (!isFirstDateEmpty && !isLastDateEmpty) {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    updatedFilters["Date Range"] = `${ toDateString(new Date(firstDate)) } - ${ toDateString(new Date(lastDate)) }`
                                    return updatedFilters
                                })
                            }
                        } }
                        hideLabel
                        fullWidth
                        dropdownPositionRight
                    />
                    { isDateRangeInvalid && <p className="text-danger fs--1 m-0 mt-1">This field cannot be cleared</p> }
                </div>

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Site</label>
                    <SearchSelect
                        width="100%"
                        options={ sites.map(item => ({ value: item.id, label: item.name })) }
                        onSelect={ value => {
                            setSelectedSite(value)
                            const selectedSiteData = sites.find(item => item.id === value)
                            setAppliedFilters(prevState => ({ ...prevState, "Site": selectedSiteData?.name }))
                        } }
                        selected={ selectedSite }
                    />
                </div>

                <div className="mb-2 d-flex flex-column">
                    <label htmlFor="date-grouping-options-selector" className="m-0 text-nowrap mb-1">Date Grouping</label>
                    <select
                        id="date-grouping-options-selector"
                        className="border rounded py-1 px-2 w-100"
                        onChange={ (e) => {
                            const parsedValue = Number(e.target.value)
                            setSelectedDateGrouping(parsedValue)
                            setAppliedFilters(prevState => ({ ...prevState, "Date Grouping": DateGroupingTypeEnum[parsedValue] }))
                        } }
                        value={ String(selectedDateGrouping) }
                    >
                        { Object.entries(DateGroupingTypeEnum).map(([key, value]) => <option key={ key } value={ key }>{ value }</option>) }
                    </select>
                </div>

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Status</label>
                    <SearchSelect
                        width="100%"
                        options={ Object.entries(Enums.BreachStatus).map(([key, value]) => ({ value: key, label: value })) }
                        onSelect={ value => {
                            const parsedValue = Number(value)
                            setSelectedStatus(parsedValue)
                            setAppliedFilters(prevState => ({ ...prevState, "Status": Enums.BreachStatus[parsedValue] }))
                        } }
                        selected={ String(selectedStatus) }
                    />
                </div>
            </div>
        </SearchFiltersModal>
    )
}
